import axios from 'axios'
import storage from 'src/services/storage'
import { store } from 'src/store'
import router from 'src/router'
import { getCurrentLocale } from 'src/helpers/functions'
import i18n from '@/services/i18n'

let is403AlertShown = false
const BASE_URL = process.env.VUE_APP_API_ORGADMIN;

// Cache keys
const CACHE_KEY_USER_LOGGED = 'cache_user_logged'
const CACHE_KEY_PLAN_CURRENT = 'cache_plan_current'
const CACHE_TTL_MS = 5 * 60 * 1000 // 5 phút

function getCached(key) {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const { data, expiredAt } = JSON.parse(raw)
    if (Date.now() > expiredAt) {
      sessionStorage.removeItem(key)
      return null
    }
    return data
  } catch {
    return null
  }
}

function setCache(key, data) {
  try {
    sessionStorage.setItem(key, JSON.stringify({ data, expiredAt: Date.now() + CACHE_TTL_MS }))
  } catch { /* bỏ qua nếu sessionStorage đầy */ }
}

export function clearApiCache() {
  sessionStorage.removeItem(CACHE_KEY_USER_LOGGED)
  sessionStorage.removeItem(CACHE_KEY_PLAN_CURRENT)
}

export function invalidateUserLoggedCache() {
  sessionStorage.removeItem(CACHE_KEY_USER_LOGGED)
}

export function invalidatePlanCache() {
  sessionStorage.removeItem(CACHE_KEY_PLAN_CURRENT)
}
const language = getCurrentLocale();
const api = axios.create({
  withCredentials: true,
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': language
  }
})

api.interceptors.request.use(function (config) {
  const token = storage.get('token')

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

export function handleError(error) {
  if (!error.response) {
    return error
  }

  const status = error.response.status
  const responseData = error.response.data || {}

  // Handle 401 - Unauthenticated
  if (status === 401) {
    store.dispatch('user/logout')
  }

  // Handle 403 - Forbidden
  if ((status === 406 || status === 403) && !is403AlertShown) {
    is403AlertShown = true;
    router.push({ name: 'Home' })  
    router.app?.$toasted?.error(i18n.t("no_permission"), {
      position: "bottom-right",
      duration: 3000,
    })
  }

  if (responseData.error_code || responseData.success === false) {
    return {
      ...error.response,
      data: responseData.data || responseData
    }
  }

  return error.response
}

// Create axios instance without authentication for public endpoints
const unauthenticatedApi = axios.create({ 
  withCredentials: true,
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': language
  }
})

api.getOrFail = async function (url, payload) {
  return api.get(url, payload).catch(error => {
    if (error.response.status === 404) {
      router.push({ name: 'Home' })
      return false
    }
    throw error
  })
}

export async function listUserApi(payload) {
  const url = `${BASE_URL}/users`
  return await api.get(url, payload).catch(handleError)
}

export async function inviteUser(payload) {
  const url = `${BASE_URL}/users/invite`
  return await api.post(url, payload).catch(handleError)
}

export async function getUser(id) {
  const url = `${BASE_URL}/users/${id}`
  return await api.getOrFail(url).catch(handleError)
}

export async function updateUserApi(id, payload) {
  const url = `${BASE_URL}/users/${id}`
  return await api.put(url, payload).catch(handleError)
}

export async function getCountriesApi() {
  const url = `${BASE_URL}/countries`
  return await api.get(url).catch(handleError)
}
export async function getCountryApi(id) {
  const url = `${BASE_URL}/country/${id}`
  return await api.get(url).catch(handleError)
}
export async function getIndustriesApi() {
  const url = `${BASE_URL}/industries`
  return await api.get(url).catch(handleError)
}

export async function getEmployeeSizesApi() {
  const url = `${BASE_URL}/employee-sizes`
  return await api.get(url).catch(handleError)
}

export async function verifyEmailApi(payload) {
  const url = `${BASE_URL}/email/verify`
  return await api.post(url, payload).catch(handleError)
}

export async function sendCodeEmailApi(payload) {
  const url = `${BASE_URL}/code/email/send`
  return await api.post(url, payload).catch(handleError)
}

export async function verifyCodeEmailApi(payload) {
  const url = `${BASE_URL}/code/email/verify`
  return await api.post(url, payload).catch(handleError)
}

export async function registerCompanyApi(payload) {
  const url = `${BASE_URL}/organization/register`
  return await api.post(url, payload).catch(handleError)
}

export async function deleteUser(id) {
  const url = `${BASE_URL}/users/${id}`
  return await api.delete(url).catch(handleError)
}

export async function resetPasswordApi(id) {
  const url = `${BASE_URL}/users/${id}/password/reset`
  return await api.put(url).catch(handleError)
}

export async function updateStatusUserApi(id, status) {
  const url = `${BASE_URL}/users/${id}/status/update`
  return await api.put(url, status).catch(handleError)
}

export async function getUserLoggedApi(force = false) {
  if (!force) {
    const cached = getCached(CACHE_KEY_USER_LOGGED)
    if (cached) return cached
  }
  const url = `${BASE_URL}/user/logged`
  const res = await api.get(url).catch(handleError)
  if (res && res.status === 200) setCache(CACHE_KEY_USER_LOGGED, res)
  return res
}

export async function loginAsOrganizationAdminApi() {
  const url = `${BASE_URL}/organization/verify_login`;
  return await api.get(url).catch(handleError)
}

export async function listConversationApi(payload) {
  const url = `${BASE_URL}/conversations`
  return await api.get(url, payload).catch(handleError)
}

export async function createConversationApi(payload) {
  const url = `${BASE_URL}/conversations`
  return await api.post(url, payload).catch(handleError)
}

export async function updateConversationApi(payload) {
  const url = `${BASE_URL}/conversations/${payload._id}`
  return await api.put(url, payload).catch(handleError)
}

export async function getConversationApi(id) {
  const url = `${BASE_URL}/conversations/${id}`
  return await api.getOrFail(url).catch(handleError)
}

export async function deleteConversationApi(id) {
  const url = `${BASE_URL}/conversations/${id}`
  return await api.delete(url).catch(handleError)
}

export async function exportConversationApi(id) {
  const url = `${BASE_URL}/export/conversations/${id}`
  return await api.get(url).catch(handleError)
}

export async function checkAdminConversationApi(id) {
  const url = `${BASE_URL}/admins/conversations/${id}`
  return await api.get(url).catch(handleError)
}

export async function getListUserOptionApi(params = {}) {
  const url = `${BASE_URL}/options/users`
  return await api.get(url, { params }).catch(handleError)
}

export async function getListUserGroupApi(payload) {
  const url = `${BASE_URL}/users/list/group`
  return await api.get(url, payload).catch(handleError)
}

export async function countStatusConversationApi() {
  const url = `${BASE_URL}/count/conversation`
  return await api.get(url).catch(handleError)
}

export async function filePresigned(payload) {
  const url = `${BASE_URL}/org/presigned`
  return await api.post(url, payload).catch(handleError)
}

export async function updateOrganizationApi(payload) {
  const url = `${BASE_URL}/organization`
  return await api.put(url, payload).catch(handleError)
}

export async function getAllCompanyUserExceptAdminApi(params = {}) {
  const url = `${BASE_URL}/organization/users-except-admin`
  return await api.get(url, { params }).catch(handleError)
}

export async function getAllCompanyUserApi(params = {}) {
  const url = `${BASE_URL}/organization/users`
  return await api.get(url, { params }).catch(handleError)
}

export async function transferOwnerApi(payload) {
  const url = `${BASE_URL}/owner/transfer`
  return await api.post(url, payload).catch(handleError)
}

export async function getListStoragesApi() {
  const url = `${BASE_URL}/storages`
  return await api.get(url).catch(handleError)
}

export async function getCurrentStoragesApi(id) {
  const url = `${BASE_URL}/storages/current`
  return await api.get(url).catch(handleError)
}

export async function getCurrentStorageQuotaStatusApi(id) {
  const url = `${BASE_URL}/storages/current/quota-status`
  return await api.get(url).catch(handleError)
}

export async function getStoragesApi(id) {
  const url = `${BASE_URL}/storages/${id}`
  return await api.get(url).catch(handleError)
}

export async function getStoragesUpgradeDetailApi(id, payload) {
  const url = `${BASE_URL}/storages/${id}/payment/detail/${payload}`
  return await api.get(url).catch(handleError)
}

export async function upgradeStorageApi(payload) {
  const url = `${BASE_URL}/payment/storage/change`
  return await api.post(url, payload).catch(handleError)
}

export async function getListContactUserApi(id, payload) {
  const url = `${BASE_URL}/users/${id}/contact`
  return await api.getOrFail(url, payload).catch(handleError)
}

export async function exportListContactUserApi(id) {
  const url = `${BASE_URL}/users/${id}/contact/export`
  return await api.get(url).catch(handleError)
}

export async function updateSecurity(payload) {
  const url = `${BASE_URL}/security/update`
  return await api.put(url, payload).catch(handleError)
}

export async function updateGuestVerification(payload) {
  const url = `${BASE_URL}/guest-verification/update`
  return await api.put(url, payload).catch(handleError)
}

// Các nhánh tách khỏi /user/logged (lazy-load theo trang) — xem
// docs/guides/user-logged-payload-optimization.md
export async function getGuestVerificationApi() {
  const url = `${BASE_URL}/organization/guest-verification`
  return await api.get(url).catch(handleError)
}

export async function getSecuritySettingsApi() {
  const url = `${BASE_URL}/organization/security-settings`
  return await api.get(url).catch(handleError)
}

export async function getProfileDetailApi() {
  const url = `${BASE_URL}/organization/profile-detail`
  return await api.get(url).catch(handleError)
}

export async function validateIpApi(payload) {
  const url = `${BASE_URL}/validate-ip`
  return await api.post(url, payload).catch(handleError)
}

export async function updateLimitFileApi(payload) {
  const url = `${BASE_URL}/user/limit-file`
  return await api.put(url, payload).catch(handleError)
}

export async function updateLimitIpApi(payload) {
  const url = `${BASE_URL}/user/limit-ip`
  return await api.put(url, payload).catch(handleError)
}

export async function listUserWithSecurityApi(payload) {
  const url = `${BASE_URL}/security/users`
  return await api.get(url, payload).catch(handleError)
}

export async function listEndpointApi(payload) {
  const url = `${BASE_URL}/endpoints`
  return await api.get(url, payload).catch(handleError)
}

export async function deleteEndpointApi(payload) {
  const url = `${BASE_URL}/endpoints/${payload.device_id}?user_id=${payload.user_id}`
  return await api.delete(url).catch(handleError)
}

export async function updateDeviceApi(payload) {
  const url = `${BASE_URL}/endpoints/${payload.device_id}?user_id=${payload.user_id}`
  return await api.put(url).catch(handleError)
}

export async function cancelRequestApi(payload) {
  const url = `${BASE_URL}/endpoints/${payload.device_id}/cancel?user_id=${payload.user_id}`
  return await api.put(url).catch(handleError)
}

export async function getCurrentPlanDetail() {
  const cached = getCached(CACHE_KEY_PLAN_CURRENT)
  if (cached) return cached
  const url = `${BASE_URL}/plans/current`
  const res = await api.get(url).catch(handleError)
  if (res && res.status === 200) setCache(CACHE_KEY_PLAN_CURRENT, res)
  return res
}

export async function getCurrentTimeTrial(id) {
  const url = `${BASE_URL}/trials/${id}`
  return await api.get(url).catch(handleError)
}

export async function getPlanList() {
  const url = `${BASE_URL}/plans`
  return await api.get(url).catch(handleError)
}

export async function getPlanToUpgrade(id) {
  const url = `${BASE_URL}/plans/${id}`
  return await api.get(url).catch(handleError)
}

export async function getListCard(limit = null, after = null, before = null) {
  const url = `${BASE_URL}/payment/cards`
  return await api.get(url, {
    params: {
      limit, after, before
    }
  }).catch(handleError)
}

export async function getSecretClientApi(payload) {
  const url = `${BASE_URL}/payment/secret-key`
  return await api.get(url, payload).catch(handleError)
}

export async function getSecretUserApi(payload) {
  const url = `${BASE_URL}/payment/secret-key-user`
  return await api.get(url, payload).catch(handleError)
}

export async function getSecretKeyNoAuthApi(body) {
  const url = `${BASE_URL}/get-secret-key-no-auth`
  return await unauthenticatedApi.post(url, { ...body }).catch(handleError)
}

export async function deleteOrganizationApi(orgId) {
  const url = `${BASE_URL}/organization/delete/${orgId}`;
  return await unauthenticatedApi.post(url).catch(handleError)
}

export async function getStripeApi(id) {
  const url = `${BASE_URL}/stripe/customer/${id}`
  return await api.get(url).catch(handleError)
}

export async function getPlanUpgradeDetail(id, period, licenses = null) {
  const url = `${BASE_URL}/payment/plan/detail/${id}/${period}` + (licenses === null ? '' : `/${licenses}`)
  return await api.get(url).catch(handleError)
}

export async function switchPlanToYearly() {
  const url = `${BASE_URL}/payment/plan/switch-yearly`
  return await api.post(url).catch(handleError)
}

export async function upgradePlan(id, period, card, licenses, cvc, error_number) {
  const url = `${BASE_URL}/payment/plan/change`
  return await api.post(url, {
    id, period, card, licenses, cvc, error_number
  }).catch(handleError)
}

export async function upgradePlanFree() {
  const url = `${BASE_URL}/payment/plan-free/change`
  return await api.post(url, {}).catch(handleError)
}

export async function getOptionStopServiceApi() {
  const url = `${BASE_URL}/plans/options/stop-service`
  return await api.get(url).catch(handleError)
}

export async function requestWithdrawServiceApi() {
  const url = `${BASE_URL}/plans/request/withdraw-service`
  return await api.get(url).catch(handleError)
}

export async function getHistoryServiceApi() {
  const url = `${BASE_URL}/plans/request/get-history-service`
  return await api.get(url).catch(handleError)
}

export async function requestStopServiceApi(payload) {
  const url = `${BASE_URL}/plans/request/stop-service`
  return await api.post(url, payload).catch(handleError)
}

export async function setPaymentMethodDefaultApi(id) {
  const url = `${BASE_URL}/payment/payment-method/${id}/default`
  return await api.post(url).catch(handleError)
}

export async function deletePaymentMethodApi(id) {
  const url = `${BASE_URL}/payment/payment-method/${id}`
  return await api.delete(url).catch(handleError)
}

export async function getListPaymentApi(payload) {
  const url = `${BASE_URL}/payment`
  return await api.get(url, payload).catch(handleError)
}

export async function getListHousesApi(payload) {
  const url = `${BASE_URL}/room-sample`
  return await api.get(url, payload).catch(handleError)
}

export async function deleteHouseApi(id) {
  const url = `${BASE_URL}/room-sample/${id}`
  return await api.delete(url).catch(handleError)
}

export async function getOneHouseApi(id) {
  const url = `${BASE_URL}/room-sample/${id}`
  return await api.get(url).catch(handleError)
}

export async function getPaymentApi(id) {
  const url = `${BASE_URL}/payment/${id}`
  return await api.getOrFail(url).catch(handleError)
}

export async function requestCancelPaymentApi(id) {
  const url = `${BASE_URL}/payment/request-cancel/${id}`
  return await api.post(url).catch(handleError)
}

export async function updateLicensesApi(payload) {
  const url = `${BASE_URL}/plans/licenses`
  return await api.put(url, payload).catch(handleError)
}
export async function updateRoomSpaceLicensesApi(payload) {
  const url = `${BASE_URL}/payment/room-space/downgrade`
  return await api.post(url, payload).catch(handleError)
}

export async function updateGuestAccessLicensesApi(payload) {
  const url = `${BASE_URL}/payment/guest-access/downgrade`
  return await api.post(url, payload).catch(handleError)
}

export async function getPaymentLicensesDetailApi(licenses) {
  const url = `${BASE_URL}/payment/licenses/detail/${licenses}`
  return await api.get(url).catch(handleError)
}
export async function getRoomSpaceLicensesPaymentDetailApi(licenses, period) {
  const url = `${BASE_URL}/room/room-space/${licenses}/${period}`
  return await api.get(url).catch(handleError)
}
export async function getPaymentObjectsDetailApi(payload) {
  const url = `${BASE_URL}/payment/get-object/detail`
  return await api.post(url, payload).catch(handleError)
}
export async function getPaymentGuestLicensesDetailApi(licenses, period) {
  const url = `${BASE_URL}/payment/guest-licenses/detail/${licenses}/${period}`
  return await api.get(url).catch(handleError)
}
export async function changeLicensesApi(payload) {
  const url = `${BASE_URL}/payment/licenses/change`
  return await api.post(url, payload).catch(handleError)
}

export async function purchaseItemsApi(payload) {
  const url = `${BASE_URL}/room-item/purchase-master`
  return await api.post(url, payload).catch(handleError)
}

export async function rentRoomApi(payload) {
  const url = `${BASE_URL}/stripe/subscription/rent-room`
  return await api.post(url, payload).catch(handleError)
}

export async function purchaseSampleRoomApi(payload) {
  const url = `${BASE_URL}/stripe/subscription/purchase-sample-room`
  return await api.post(url, payload).catch(handleError)
}

export async function retryPaymentApi(payload) {
  const url = `${BASE_URL}/payment/invoice/repay`
  return await api.post(url, payload).catch(handleError)
}

export async function getNewCirclePaymentApi(id) {
  const url = `${BASE_URL}/payment/invoice/new-circle/${id}`
  return await api.get(url).catch(handleError)
}

export async function newCirclePaymentApi(payload) {
  const url = `${BASE_URL}/payment/invoice/new-circle`
  return await api.post(url, payload).catch(handleError)
}

export async function createServiceApi(payload) {
  const url = `${BASE_URL}/services`
  return await api.post(url, payload).catch(handleError)
}

export async function getServicesApi(payload) {
  const url = `${BASE_URL}/services`
  return await api.get(url, payload).catch(handleError)
}

export async function deleteServiceApi(id) {
  const url = `${BASE_URL}/services/${id}`
  return await api.delete(url).catch(handleError)
}

export async function exportPrChannelApi(id) {
  const url = `${BASE_URL}/export/pr-channels/${id}`
  return await api.get(url).catch(handleError)
}

export async function getServiceApi(id) {
  const url = `${BASE_URL}/services/${id}`
  return await api.getOrFail(url).catch(handleError)
}

export async function updateServiceApi(id, payload) {
  const url = `${BASE_URL}/services/${id}`
  return await api.put(url, payload).catch(handleError)
}

export async function getCapacityOrganizationApi() {
  const url = `${BASE_URL}/capacity/organization`
  return await api.get(url).catch(handleError)
}

export async function getLanguageApi(payload) {
  const url = `${BASE_URL}/language`
  return await api.get(url, payload).catch(handleError)
}

export async function changePasswordApi(payload) {
  const url = `${BASE_URL}/change-password`
  return await api.post(url, payload).catch(handleError)
}

export async function resetPasswordOrganizationApi(payload) {
  const url = `${BASE_URL}/reset-password`
  return await api.post(url, payload).catch(handleError)
}

export async function verifyPasswordApi(payload) {
  const url = `${BASE_URL}/verify-password`
  return await api.post(url, payload).catch(handleError)
}

export async function changePasswordSuccessApi(email) {
  const url = `${BASE_URL}/password/change/success/${email}`
  return await api.post(url).catch(handleError)
}

export async function userChangePasswordSuccessApi(email) {
  const url = `${BASE_URL}/user/password/change/success/${email}`
  return await api.get(url).catch(handleError)
}

export async function logLoginHistoryApi() {
  const url = `${BASE_URL}/log/login`
  return await api.post(url).catch(handleError)
}

export async function logLogoutHistoryApi() {
  const url = `${BASE_URL}/log/logout`
  return await api.post(url).catch(handleError)
}

export async function logAddPaymentMethodApi() {
  const url = `${BASE_URL}/log/add-payment-method`
  return await api.post(url).catch(handleError)
}

export async function checkDuplicateCardApi(payload) {
  const url = `${BASE_URL}/check-duplicate-card`
  return await unauthenticatedApi.post(url, payload).catch(handleError)
}

export async function downloadImageApi(id) {
  const url = `${BASE_URL}/${id}/download`
  return await api.get(url, {
    responseType: 'arraybuffer'
  }).catch(handleError)
}

export async function updateLanguage(payload) {
  const url = `${BASE_URL}/organization/language/update`
  return await api.put(url, payload).catch(handleError)
}

export async function list2FAUsersApi(payload) {
  const url = `${BASE_URL}/security/2fa/users`
  return await api.get(url, payload).catch(handleError)
}

export async function requestChange2FAMethodApi(payload) {
  const url = `${BASE_URL}/security/2fa/request-change-to-totp`
  return await api.post(url, payload).catch(handleError)
}

export async function initiate2FASetupApi(token) {
  const url = `${BASE_URL}/security/2fa/initiate-totp-setup?token=${token}`
  // This endpoint doesn't require authentication
  return await unauthenticatedApi.post(url).catch(error => {
    return error.response || { status: 500, data: { message: 'Network error' } }
  })
}

export async function complete2FASetupApi(payload) {
  const url = `${BASE_URL}/security/2fa/complete-totp-setup`
  // This endpoint doesn't require authentication
  // Convert totp_code to code as per API spec
  const requestPayload = {
    token: payload.token,
    code: payload.totp_code || payload.code
  }
  return await unauthenticatedApi.post(url, requestPayload).catch(error => {
    return error.response || { status: 500, data: { message: 'Network error' } }
  })
}

export async function resetUser2FASetupApi(payload) {
  const url = `${BASE_URL}/security/2fa/reset-user-totp-setup`
  return await api.post(url, payload).catch(handleError)
}

export async function licensesHistories() {
  const url = `${BASE_URL}/licenses-history`
  return await api.get(url).catch(handleError)
}

export async function guestAccessHistories() {
  const url = `${BASE_URL}/guest-access-history`
  return await api.get(url).catch(handleError)
}

export async function roomSpaceHistories() {
  const url = `${BASE_URL}/room-space-history`
  return await api.get(url).catch(handleError)
}

export async function objectHistories(id) {
  const url = `${BASE_URL}/room-item/room-object-history/${id}`
  return await api.get(url, id).catch(handleError)
}

export async function roomSampleHistories(id) {
  const url = `${BASE_URL}/room-sample/history/${id}`
  return await api.get(url, id).catch(handleError)
}
